import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {AlertCircle, BarChart, Calendar, CheckCircle, FileText, Plus, X} from 'lucide-react';

const AnnualPedagogicalPlan = () => {
    const navigate = useNavigate();

    // State for active tab
    const [activeTab, setActiveTab] = useState('overview');

    // State for notification
    const [notification, setNotification] = useState(null);

    // Calendar state
    const [currentMonthIndex, setCurrentMonthIndex] = useState(11); // December
    const [currentYear, setCurrentYear] = useState(2024);

    // Learning Outcomes Mapping table filters
    const [outcomesGradeFilter, setOutcomesGradeFilter] = useState('');
    const [outcomesSubjectFilter, setOutcomesSubjectFilter] = useState('');

    // Assessment Schedule table filters
    const [assessmentTypeFilter, setAssessmentTypeFilter] = useState('');
    const [assessmentGradeFilter, setAssessmentGradeFilter] = useState('');
    const [assessmentStatusFilter, setAssessmentStatusFilter] = useState('');
    const [assessmentDateStartFilter, setAssessmentDateStartFilter] = useState('');
    const [assessmentDateEndFilter, setAssessmentDateEndFilter] = useState('');

    // Teacher Allocation table filters
    const [teacherSubjectFilter, setTeacherSubjectFilter] = useState('');
    const [teacherTrainingFilter, setTeacherTrainingFilter] = useState('');

    // Curriculum Plans state
    const [curriculumPlans, setCurriculumPlans] = useState([
        {
            id: 1,
            subject: "Mathematics",
            grade: "Grade 5",
            term1: "Numbers, Basic Operations, Fractions",
            term2: "Decimals, Geometry, Measurement",
            term3: "Data Handling, Patterns, Problem Solving",
            totalUnits: 12,
            completedUnits: 9,
            teachingHours: 180,
            assessments: 8
        },
        {
            id: 2,
            subject: "English",
            grade: "Grade 5",
            term1: "Reading Comprehension, Grammar Basics",
            term2: "Creative Writing, Literature",
            term3: "Communication Skills, Poetry",
            totalUnits: 10,
            completedUnits: 8,
            teachingHours: 160,
            assessments: 6
        },
        {
            id: 3,
            subject: "Science",
            grade: "Grade 5",
            term1: "Living Things, Human Body",
            term2: "Matter & Materials, Forces",
            term3: "Environment, Simple Machines",
            totalUnits: 8,
            completedUnits: 6,
            teachingHours: 120,
            assessments: 6
        }
    ]);

    // Calendar Events state
    const [calendarEvents, setCalendarEvents] = useState([
        {date: "2024-04-01", type: "academic", title: "Academic Year Begins", description: "First day of school"},
        {date: "2024-04-15", type: "ptm", title: "Parent Orientation", description: "Annual parent meeting"},
        {date: "2024-05-01", type: "holiday", title: "Labour Day", description: "National holiday"},
        {date: "2024-05-15", type: "exam", title: "FA1 Begins", description: "First formative assessment"},
        {date: "2024-06-21", type: "activity", title: "International Yoga Day", description: "Wellness activity"},
        {date: "2024-07-15", type: "exam", title: "SA1 Begins", description: "First summative assessment"},
        {date: "2024-08-15", type: "holiday", title: "Independence Day", description: "National celebration"},
        {date: "2024-09-05", type: "ptm", title: "PTM - Term 1", description: "Parent-teacher meeting"}
    ]);

    // Teaching Methods state
    const [teachingMethods, setTeachingMethods] = useState([
        {
            name: "Activity-Based Learning",
            description: "Hands-on learning through experiments, projects, and interactive activities",
            grades: "All Grades",
            frequency: "Daily",
            subjects: "All Subjects"
        },
        {
            name: "Project-Based Learning",
            description: "Long-term projects that integrate multiple subjects and real-world applications",
            grades: "Grades 6-12",
            frequency: "Monthly",
            subjects: "Science, Social Studies"
        },
        {
            name: "Collaborative Learning",
            description: "Group work, peer teaching, and cooperative learning strategies",
            grades: "All Grades",
            frequency: "3x/week",
            subjects: "All Subjects"
        },
        {
            name: "Multilingual Scaffolding",
            description: "Using mother tongue to support learning in English and Hindi",
            grades: "Grades 1-5",
            frequency: "Daily",
            subjects: "Language, Math"
        }
    ]);

    // Co-Curricular Activities state
    const [coCurricularActivities, setCoCurricularActivities] = useState([
        {
            id: 1,
            name: "Science Exhibition",
            category: "Academic",
            date: "2024-11-15",
            participants: "Grades 6-10",
            description: "Annual science project showcase",
            status: "planning"
        },
        {
            id: 2,
            name: "Cultural Festival",
            category: "Arts",
            date: "2024-12-20",
            participants: "All Grades",
            description: "Celebration of diverse cultures",
            status: "active"
        },
        {
            id: 3,
            name: "Sports Day",
            category: "Sports",
            date: "2025-01-26",
            participants: "All Grades",
            description: "Annual athletic competition",
            status: "planning"
        }
    ]);

    // Support Programs state
    const [supportPrograms, setSupportPrograms] = useState([
        {
            name: "Reading Recovery Program",
            target: "Struggling readers in Grades 1-3",
            frequency: "Daily 30 min sessions",
            staff: "2 trained teachers",
            resources: "Leveled books, phonics materials"
        },
        {
            name: "Math Remediation",
            target: "Students below grade level in numeracy",
            frequency: "3x/week after school",
            staff: "Math coordinator + volunteers",
            resources: "Manipulatives, digital tools"
        },
        {
            name: "Language Bridge Program",
            target: "Non-native speakers",
            frequency: "Daily during language period",
            staff: "Bilingual teachers",
            resources: "Translation tools, cultural materials"
        }
    ]);

    // Teacher Allocation state
    const [teacherAllocation, setTeacherAllocation] = useState([
        {
            name: "Mrs. Priya Sharma",
            subject: "Mathematics",
            classes: "5A, 5B, 6A",
            duties: "Math Club Coordinator",
            training: "Completed",
            workload: "85%"
        },
        {
            name: "Mr. Rajesh Kumar",
            subject: "English",
            classes: "7A, 7B, 8A",
            duties: "Literary Society Head",
            training: "In Progress",
            workload: "90%"
        },
        {
            name: "Ms. Anita Patel",
            subject: "Science",
            classes: "9A, 9B, 10A",
            duties: "Science Lab In-charge",
            training: "Completed",
            workload: "88%"
        }
    ]);

    // Assessment data
    const [assessments, setAssessments] = useState([
        {
            date: "2024-12-15",
            type: "FA2",
            grade: "Grade 5",
            subject: "Mathematics",
            duration: "1 hour",
            weightage: "10%",
            status: "scheduled"
        },
        {
            date: "2024-12-18",
            type: "SA2",
            grade: "Grade 5",
            subject: "English",
            duration: "2 hours",
            weightage: "30%",
            status: "scheduled"
        },
        {
            date: "2024-12-20",
            type: "Practical",
            grade: "Grade 5",
            subject: "Science",
            duration: "1.5 hours",
            weightage: "20%",
            status: "scheduled"
        }
    ]);

    // Progress indicators for monitoring tab
    const [progressIndicators, setProgressIndicators] = useState([
        {
            name: "Curriculum Coverage Rate",
            current: "78%",
            target: "90%",
            trend: "up",
            description: "Percentage of planned curriculum completed"
        },
        {
            name: "Student Learning Outcomes",
            current: "73%",
            target: "85%",
            trend: "up",
            description: "Students meeting grade-level competencies"
        },
        {
            name: "Teacher Training Completion",
            current: "92%",
            target: "100%",
            trend: "up",
            description: "Teachers completed required professional development"
        },
        {
            name: "Assessment Quality Score",
            current: "4.2/5",
            target: "4.5/5",
            trend: "stable",
            description: "Quality of assessment practices and feedback"
        }
    ]);

    // Activity counts for cards
    const activityCounts = {
        artsActivities: 12,
        sportsActivities: 8,
        communityActivities: 6,
        clubActivities: 15
    };

    // Pedagogical frameworks
    const [pedagogicalFrameworks, setPedagogicalFrameworks] = useState([
        {
            grade: "Grades 1-2",
            focus: "Play-based Learning",
            methods: "Stories, Songs, Games, Art",
            assessment: "Observation, Portfolio"
        },
        {
            grade: "Grades 3-5",
            focus: "Activity-based Learning",
            methods: "Experiments, Projects, Group work",
            assessment: "Performance tasks, Presentations"
        },
        {
            grade: "Grades 6-8",
            focus: "Inquiry-based Learning",
            methods: "Research, Debates, Problem solving",
            assessment: "Projects, Peer assessment"
        },
        {
            grade: "Grades 9-12",
            focus: "Critical Thinking",
            methods: "Analysis, Synthesis, Evaluation",
            assessment: "Essays, Research papers"
        }
    ]);

    // Show the tab content based on active tab
    const showTab = (tabName) => {
        setActiveTab(tabName);
    };

    // Update calendar month display
    useEffect(() => {
        if (activeTab === 'calendar') {
            renderCalendar();
        }
    }, [activeTab, currentMonthIndex, currentYear, calendarEvents]);

    // Show notification
    const showNotification = (message, type) => {
        setNotification({message, type});
        setTimeout(() => {
            setNotification(null);
        }, 3000);
    };

    // Toggle indicator trend
    const toggleIndicatorTrend = (index) => {
        const trends = ['up', 'stable', 'down'];
        const newIndicators = [...progressIndicators];
        const currentTrend = newIndicators[index].trend;
        const currentIndex = trends.indexOf(currentTrend);
        const nextIndex = (currentIndex + 1) % trends.length;

        newIndicators[index].trend = trends[nextIndex];
        setProgressIndicators(newIndicators);
        showNotification('Indicator trend updated!', 'success');
    };

    // Calendar navigation
    const previousMonth = () => {
        setCurrentMonthIndex((prev) => {
            if (prev === 0) {
                setCurrentYear(currentYear - 1);
                return 11;
            }
            return prev - 1;
        });
    };

    const nextMonth = () => {
        setCurrentMonthIndex((prev) => {
            if (prev === 11) {
                setCurrentYear(currentYear + 1);
                return 0;
            }
            return prev + 1;
        });
    };

    // Inline editing functionality
    const editInline = (element) => {
        const originalText = element.textContent || element.innerText;
        const isTextarea = originalText.length > 50;

        if (isTextarea) {
            const textarea = document.createElement('textarea');
            textarea.value = originalText;
            textarea.className = 'w-full p-2 border border-[#D67C2C] rounded focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent resize-none';
            textarea.rows = Math.min(Math.ceil(originalText.length / 50), 4);

            element.parentNode.replaceChild(textarea, element);
            textarea.focus();
            textarea.select();

            function saveEdit() {
                const newText = textarea.value.trim();
                if (newText && newText !== originalText) {
                    element.textContent = newText;
                    showNotification('Content updated successfully!', 'success');
                } else {
                    element.textContent = originalText;
                }
                textarea.parentNode.replaceChild(element, textarea);
            }

            textarea.addEventListener('blur', saveEdit);
            textarea.addEventListener('keydown', function (e) {
                if (e.key === 'Enter' && e.ctrlKey) {
                    saveEdit();
                } else if (e.key === 'Escape') {
                    element.textContent = originalText;
                    textarea.parentNode.replaceChild(element, textarea);
                }
            });
        } else {
            const input = document.createElement('input');
            input.type = 'text';
            input.value = originalText;
            input.className = 'w-full p-2 border border-[#D67C2C] rounded focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent';

            element.parentNode.replaceChild(input, element);
            input.focus();
            input.select();

            function saveEdit() {
                const newText = input.value.trim();
                if (newText && newText !== originalText) {
                    element.textContent = newText;
                    showNotification('Content updated successfully!', 'success');
                } else {
                    element.textContent = originalText;
                }
                input.parentNode.replaceChild(element, input);
            }

            input.addEventListener('blur', saveEdit);
            input.addEventListener('keydown', function (e) {
                if (e.key === 'Enter') {
                    saveEdit();
                } else if (e.key === 'Escape') {
                    element.textContent = originalText;
                    input.parentNode.replaceChild(element, input);
                }
            });
        }
    };

    // Action functions
    const addCurriculumPlan = () => {
        const subject = prompt('Enter subject name:');
        if (!subject) return;

        const grade = prompt('Enter grade:');
        if (!grade) return;

        const newPlan = {
            id: Date.now(),
            subject: subject,
            grade: grade,
            term1: 'Click to add Term 1 topics',
            term2: 'Click to add Term 2 topics',
            term3: 'Click to add Term 3 topics',
            totalUnits: 10,
            completedUnits: 0,
            teachingHours: 120,
            assessments: 6
        };

        setCurriculumPlans([...curriculumPlans, newPlan]);
        showNotification(`Curriculum plan for ${subject} - ${grade} added successfully!`, 'success');
    };

    const addAssessment = () => {
        const type = prompt('Enter assessment type (FA1, SA1, etc.):');
        if (!type) return;

        const subject = prompt('Enter subject:');
        if (!subject) return;

        const date = prompt('Enter date (YYYY-MM-DD):');
        if (!date) return;

        const newAssessment = {
            date: date,
            type: type,
            grade: "Grade 5",
            subject: subject,
            duration: "1 hour",
            weightage: "10%",
            status: "scheduled"
        };

        setAssessments([...assessments, newAssessment]);
        showNotification(`Assessment ${type} for ${subject} scheduled for ${date}!`, 'success');
    };

    const addCoCurricularActivity = () => {
        const name = prompt('Enter activity name:');
        if (!name) return;

        const category = prompt('Enter category (Arts, Sports, Academic, etc.):');
        if (!category) return;

        const date = prompt('Enter date (YYYY-MM-DD):');
        if (!date) return;

        const newActivity = {
            id: Date.now(),
            name: name,
            category: category,
            date: date,
            participants: 'All Grades',
            description: 'Click to add description',
            status: 'planning'
        };

        setCoCurricularActivities([...coCurricularActivities, newActivity]);
        showNotification(`Activity "${name}" added successfully!`, 'success');
    };

    const addCalendarEvent = () => {
        const title = prompt('Enter event title:');
        if (!title) return;

        const date = prompt('Enter date (YYYY-MM-DD):');
        if (!date) return;

        const type = prompt('Enter type (exam, holiday, ptm, activity):');
        if (!type) return;

        const newEvent = {
            date: date,
            type: type,
            title: title,
            description: 'Click to add description'
        };

        setCalendarEvents([...calendarEvents, newEvent]);
        showNotification(`Event "${title}" added to calendar!`, 'success');
    };

    const schedulePTM = () => {
        const date = prompt('Enter PTM date (YYYY-MM-DD):');
        if (!date) return;

        const grade = prompt('Enter grade/class:');
        if (!grade) return;

        showNotification(`PTM scheduled for ${grade} on ${date}!`, 'success');
    };

    const assignTeacherRole = () => {
        const teacher = prompt('Enter teacher name:');
        if (!teacher) return;

        const role = prompt('Enter additional role/duty:');
        if (!role) return;

        showNotification(`Role "${role}" assigned to ${teacher}!`, 'success');
    };

    const exportAPP = () => {
        showNotification('Exporting Annual Pedagogical Plan...', 'info');
        setTimeout(() => {
            showNotification('APP exported successfully!', 'success');
        }, 2000);
    };

    const generateReport = () => {
        showNotification('Generating APP progress report...', 'info');
        setTimeout(() => {
            showNotification('Progress report generated successfully!', 'success');
        }, 2000);
    };

    // Render functions for different sections
    const renderCalendar = () => {
        // For now, a placeholder since actual DOM manipulation will be replaced with React JSX
        return true;
    };

    const renderSampleCalendarMonth = () => {
        const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

        // Header
        const calendarHeader = days.map((day, index) => (
            <div key={`header-${index}`}
                 className="calendar-day font-semibold text-center bg-gray-100 text-gray-700 py-2">
                {day}
            </div>
        ));

        // Sample days for the current month (simplified)
        const months = ['January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'];
        const currentMonth = months[currentMonthIndex];

        // Just generating 30 days for simplicity
        const calendarDays = Array.from({length: 30}, (_, i) => {
            const day = i + 1;
            const dayEvents = calendarEvents.filter(event =>
                new Date(event.date).getDate() === day &&
                new Date(event.date).getMonth() === currentMonthIndex
            );

            return (
                <div key={`day-${day}`} className="calendar-day">
                    <div className="font-medium text-sm">{day}</div>
                    {dayEvents.map((event, eventIndex) => (
                        <div
                            key={`event-${day}-${eventIndex}`}
                            className={`calendar-event ${
                                event.type === 'exam' ? 'bg-red-100 text-red-800' :
                                    event.type === 'holiday' ? 'bg-gray-100 text-gray-800' :
                                        event.type === 'ptm' ? 'bg-blue-100 text-blue-800' :
                                            'bg-green-100 text-green-800'
                            }`}
                            title={event.description}
                        >
                            {event.title}
                        </div>
                    ))}
                </div>
            );
        });

        return [...calendarHeader, ...calendarDays];
    };

    // Learning Outcomes Mapping table - data & filters
    const learningOutcomesData = [
        {
            grade: "Grade 5",
            subject: "Mathematics",
            unit: "Fractions",
            outcomes: "Compare and order fractions, Add/subtract like fractions",
            assessment: "Problem solving, Practical activities",
            timeline: "Week 8-10"
        },
        {
            grade: "Grade 5",
            subject: "English",
            unit: "Reading Comprehension",
            outcomes: "Identify main idea, Make inferences, Summarize text",
            assessment: "Reading tests, Oral presentation",
            timeline: "Week 5-7"
        },
        {
            grade: "Grade 5",
            subject: "Science",
            unit: "Living Things",
            outcomes: "Classify living/non-living, Understand life processes",
            assessment: "Observation sheets, Group projects",
            timeline: "Week 3-5"
        }
    ];
    const outcomesGradeOptions = Array.from(new Set(learningOutcomesData.map(outcome => outcome.grade))).filter(Boolean);
    const outcomesSubjectOptions = Array.from(new Set(learningOutcomesData.map(outcome => outcome.subject))).filter(Boolean);
    const filteredLearningOutcomes = learningOutcomesData.filter(outcome => {
        if (outcomesGradeFilter && outcome.grade !== outcomesGradeFilter) return false;
        if (outcomesSubjectFilter && outcome.subject !== outcomesSubjectFilter) return false;
        return true;
    });

    // Assessment Schedule table - filters
    const assessmentTypeOptions = Array.from(new Set(assessments.map(assessment => assessment.type))).filter(Boolean);
    const assessmentGradeOptions = Array.from(new Set(assessments.map(assessment => assessment.grade))).filter(Boolean);
    const assessmentStatusOptions = Array.from(new Set(assessments.map(assessment => assessment.status))).filter(Boolean);
    const filteredAssessments = assessments.filter(assessment => {
        if (assessmentTypeFilter && assessment.type !== assessmentTypeFilter) return false;
        if (assessmentGradeFilter && assessment.grade !== assessmentGradeFilter) return false;
        if (assessmentStatusFilter && assessment.status !== assessmentStatusFilter) return false;
        if (assessmentDateStartFilter && assessment.date < assessmentDateStartFilter) return false;
        if (assessmentDateEndFilter && assessment.date > assessmentDateEndFilter) return false;
        return true;
    });

    // Teacher Allocation table - filters
    const teacherSubjectOptions = Array.from(new Set(teacherAllocation.map(teacher => teacher.subject))).filter(Boolean);
    const teacherTrainingOptions = Array.from(new Set(teacherAllocation.map(teacher => teacher.training))).filter(Boolean);
    const filteredTeacherAllocation = teacherAllocation.filter(teacher => {
        if (teacherSubjectFilter && teacher.subject !== teacherSubjectFilter) return false;
        if (teacherTrainingFilter && teacher.training !== teacherTrainingFilter) return false;
        return true;
    });

    return (
        <>
            <div className="bg-white p-8 min-h-screen rounded-b-2xl rounded-tr-2xl shadow-xl font-[poppins]">
                <div className="w-full px-4 sm:px-6 lg:px-8 py-8">

                    {/* Notification */}
                    {notification && (
                        <div
                            className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg flex items-center gap-3 transition-all duration-300 ${
                                notification.type === 'success' ? 'bg-green-500 text-white' :
                                    notification.type === 'error' ? 'bg-red-500 text-white' :
                                        'bg-blue-500 text-white'
                            }`} style={{animation: 'slideInRight 0.5s ease-out'}}>
                            {notification.type === 'success' ? <CheckCircle className="w-5 h-5"/> :
                                notification.type === 'error' ? <AlertCircle className="w-5 h-5"/> :
                                    <AlertCircle className="w-5 h-5"/>}
                            <span className="font-[poppins]">{notification.message}</span>
                            <button onClick={() => setNotification(null)} className="ml-2 hover:opacity-80">
                                <X className="w-4 h-4"/>
                            </button>
                        </div>
                    )}

                    {/* Header */}
                    <header className="bg-[#D67C2C] text-white shadow-lg rounded-xl mb-6">
                        <div className="container mx-auto px-6 py-4">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-3">
                                    <div className="w-10 h-10 bg-[#1D3469] rounded-lg flex items-center justify-center">
                                        <span className="text-xl font-bold text-white">APP</span>
                                    </div>
                                    <div>
                                        <h1 className="text-2xl font-bold">Annual Pedagogical Plan</h1>
                                        <p className="text-orange-100">Operationalizing School Strategy into Classroom
                                            Action</p>
                                    </div>
                                </div>
                                <div className="flex items-center space-x-4">
                                    <div className="text-right">
                                        <p className="text-sm text-orange-100">Academic Year</p>
                                        <p className="font-semibold">2024-25</p>
                                    </div>
                                    <button
                                        onClick={exportAPP}
                                        className="bg-white bg-opacity-20 hover:bg-opacity-30 px-4 py-2 rounded-lg transition-all"
                                    >
                                        Export APP
                                    </button>
                                </div>
                            </div>
                        </div>
                    </header>

                    {/* Navigation Tabs */}
                    <nav className="bg-white shadow-sm border-b rounded-t-xl mb-6">
                        <div className="container mx-auto px-6">
                            <div className="flex space-x-6 overflow-x-auto">
                                {[
                                    {id: 'overview', name: 'Overview'},
                                    {id: 'calendar', name: 'Academic Calendar'},
                                    {id: 'curriculum', name: 'Curriculum Planning'},
                                    {id: 'pedagogy', name: 'Pedagogical Strategies'},
                                    {id: 'assessment', name: 'Assessment Framework'},
                                    {id: 'cocurricular', name: 'Co-Curricular'},
                                    {id: 'inclusion', name: 'Inclusion & Support'},
                                    {id: 'teachers', name: 'Teacher Allocation'},
                                    {id: 'monitoring', name: 'Monitoring & Review'}
                                ].map(tab => (
                                    <button
                                        key={tab.id}
                                        onClick={() => showTab(tab.id)}
                                        className={`py-4 px-3 border-b-2 whitespace-nowrap ${
                                            activeTab === tab.id ?
                                                'border-[#D67C2C] text-[#D67C2C] font-medium' :
                                                'border-transparent text-gray-500 hover:text-gray-700'
                                        }`}
                                    >
                                        {tab.name}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </nav>

                    {/* Main Content */}
                    <main className="container mx-auto px-6 py-4">
                        {/* Overview Tab */}
                        {activeTab === 'overview' && (
                            <div>
                                {/* SDP Alignment */}
                                <div className="bg-white rounded-xl shadow-sm p-6 mb-8 border border-gray-200">
                                    <h2 className="text-2xl font-bold mb-6 text-[#1D3469]">SDP Strategic Alignment</h2>
                                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                        <div className="bg-blue-50 p-4 rounded-lg">
                                            <h3 className="font-semibold text-blue-700 mb-3">Academic Excellence</h3>
                                            <div className="space-y-2">
                                                <div className="flex justify-between text-sm">
                                                    <span>FLN Achievement</span>
                                                    <span className="font-medium">Target: 85%</span>
                                                </div>
                                                <div className="flex justify-between text-sm">
                                                    <span>Grade 10 Pass Rate</span>
                                                    <span className="font-medium">Target: 95%</span>
                                                </div>
                                                <div className="flex justify-between text-sm">
                                                    <span>Competency-Based Learning</span>
                                                    <span className="font-medium">100% Implementation</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="bg-green-50 p-4 rounded-lg">
                                            <h3 className="font-semibold text-green-700 mb-3">Teacher Development</h3>
                                            <div className="space-y-2">
                                                <div className="flex justify-between text-sm">
                                                    <span>Digital Competency</span>
                                                    <span className="font-medium">100% Teachers</span>
                                                </div>
                                                <div className="flex justify-between text-sm">
                                                    <span>Pedagogical Training</span>
                                                    <span className="font-medium">40 Hours/Year</span>
                                                </div>
                                                <div className="flex justify-between text-sm">
                                                    <span>Peer Collaboration</span>
                                                    <span className="font-medium">Monthly Sessions</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="bg-orange-50 p-4 rounded-lg">
                                            <h3 className="font-semibold text-orange-700 mb-3">Holistic Development</h3>
                                            <div className="space-y-2">
                                                <div className="flex justify-between text-sm">
                                                    <span>Co-curricular Participation</span>
                                                    <span className="font-medium">100% Students</span>
                                                </div>
                                                <div className="flex justify-between text-sm">
                                                    <span>Community Projects</span>
                                                    <span className="font-medium">4 Per Year</span>
                                                </div>
                                                <div className="flex justify-between text-sm">
                                                    <span>Values Integration</span>
                                                    <span className="font-medium">Daily Practice</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Key Metrics */}
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                                    <div
                                        className="bg-white rounded-xl shadow-sm p-6 card-hover border border-gray-200 transition-all duration-300 hover:shadow-lg">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="text-gray-500 text-sm">Teaching Days</p>
                                                <p className="text-3xl font-bold text-gray-900">220</p>
                                            </div>
                                            <div className="w-12 h-12 rounded-lg flex items-center justify-center"
                                                 style={{backgroundColor: 'rgba(214, 124, 44, 0.1)'}}>
                                                <div className="w-6 h-6 bg-[#D67C2C] rounded"></div>
                                            </div>
                                        </div>
                                        <div className="mt-4">
                                            <div className="flex items-center text-green-600 text-sm">
                                                <span>Planned for 2024-25</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div
                                        className="bg-white rounded-xl shadow-sm p-6 card-hover border border-gray-200 transition-all duration-300 hover:shadow-lg">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="text-gray-500 text-sm">Curriculum Coverage</p>
                                                <p className="text-3xl font-bold text-gray-900">78%</p>
                                            </div>
                                            <div className="w-12 h-12 rounded-lg flex items-center justify-center"
                                                 style={{backgroundColor: 'rgba(214, 124, 44, 0.1)'}}>
                                                <div className="w-6 h-6 bg-[#D67C2C] rounded"></div>
                                            </div>
                                        </div>
                                        <div className="mt-4">
                                            <div className="w-full bg-gray-200 rounded-full h-2">
                                                <div className="bg-[#D67C2C] h-2 rounded-full"
                                                     style={{width: '78%'}}></div>
                                            </div>
                                        </div>
                                    </div>

                                    <div
                                        className="bg-white rounded-xl shadow-sm p-6 card-hover border border-gray-200 transition-all duration-300 hover:shadow-lg">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="text-gray-500 text-sm">Assessment Points</p>
                                                <p className="text-3xl font-bold text-gray-900">156</p>
                                            </div>
                                            <div className="w-12 h-12 rounded-lg flex items-center justify-center"
                                                 style={{backgroundColor: 'rgba(214, 124, 44, 0.1)'}}>
                                                <div className="w-6 h-6 bg-[#D67C2C] rounded"></div>
                                            </div>
                                        </div>
                                        <div className="mt-4">
                                            <div className="flex items-center text-[#D67C2C] text-sm">
                                                <span>FA + SA Combined</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div
                                        className="bg-white rounded-xl shadow-sm p-6 card-hover border border-gray-200 transition-all duration-300 hover:shadow-lg">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="text-gray-500 text-sm">Teacher Readiness</p>
                                                <p className="text-3xl font-bold text-gray-900">92%</p>
                                            </div>
                                            <div className="w-12 h-12 rounded-lg flex items-center justify-center"
                                                 style={{backgroundColor: 'rgba(214, 124, 44, 0.1)'}}>
                                                <div className="w-6 h-6 bg-[#D67C2C] rounded-full"></div>
                                            </div>
                                        </div>
                                        <div className="mt-4">
                                            <div className="flex items-center text-[#D67C2C] text-sm">
                                                <span>Training Completed</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* APP Functions Overview */}
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                                    <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                                        <h3 className="text-lg font-semibold mb-4 text-[#1D3469]">APP Implementation
                                            Status</h3>
                                        <div className="space-y-4">
                                            <div className="flex justify-between items-center">
                                                <span className="text-gray-600">Curriculum Distribution</span>
                                                <span
                                                    className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">Complete</span>
                                            </div>
                                            <div className="flex justify-between items-center">
                                                <span className="text-gray-600">Pedagogical Framework</span>
                                                <span
                                                    className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">Complete</span>
                                            </div>
                                            <div className="flex justify-between items-center">
                                                <span className="text-gray-600">Assessment Integration</span>
                                                <span
                                                    className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">In Progress</span>
                                            </div>
                                            <div className="flex justify-between items-center">
                                                <span className="text-gray-600">Teacher Role Allocation</span>
                                                <span
                                                    className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">In Progress</span>
                                            </div>
                                            <div className="flex justify-between items-center">
                                                <span className="text-gray-600">Monitoring Framework</span>
                                                <span
                                                    className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm">Planning</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                                        <h3 className="text-lg font-semibold mb-4 text-[#1D3469]">Key Focus Areas
                                            (2024-25)</h3>
                                        <div className="space-y-3">
                                            <div className="flex items-center space-x-3">
                                                <div className="w-3 h-3 bg-[#D67C2C] rounded-full"></div>
                                                <span>Foundational Literacy & Numeracy (Grades 1-3)</span>
                                            </div>
                                            <div className="flex items-center space-x-3">
                                                <div className="w-3 h-3 bg-[#D67C2C] rounded-full"></div>
                                                <span>Competency-Based Learning Implementation</span>
                                            </div>
                                            <div className="flex items-center space-x-3">
                                                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                                                <span>Multilingual Education & Mother Tongue</span>
                                            </div>
                                            <div className="flex items-center space-x-3">
                                                <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                                                <span>Technology-Enhanced Learning</span>
                                            </div>
                                            <div className="flex items-center space-x-3">
                                                <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                                                <span>Experiential & Project-Based Learning</span>
                                            </div>
                                            <div className="flex items-center space-x-3">
                                                <div className="w-3 h-3 bg-[#1D3469] rounded-full"></div>
                                                <span>Inclusive Education & SEN Support</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Quick Actions */}
                                <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                                    <h3 className="text-lg font-semibold mb-4 text-[#1D3469]">Quick Actions</h3>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                        <button
                                            onClick={addCurriculumPlan}
                                            className="p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-[#D67C2C] hover:bg-orange-50 transition-all text-center"
                                        >
                                            <div
                                                className="w-8 h-8 mx-auto mb-2 rounded-lg flex items-center justify-center"
                                                style={{backgroundColor: 'rgba(214, 124, 44, 0.1)'}}>
                                                <Plus className="w-4 h-4 text-[#D67C2C]"/>
                                            </div>
                                            <p className="font-medium text-sm">Add Curriculum Plan</p>
                                        </button>
                                        <button
                                            onClick={schedulePTM}
                                            className="p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-[#D67C2C] hover:bg-orange-50 transition-all text-center"
                                        >
                                            <div
                                                className="w-8 h-8 mx-auto mb-2 rounded-lg flex items-center justify-center"
                                                style={{backgroundColor: 'rgba(214, 124, 44, 0.1)'}}>
                                                <Calendar className="w-4 h-4 text-[#D67C2C]"/>
                                            </div>
                                            <p className="font-medium text-sm">Schedule PTM</p>
                                        </button>
                                        <button
                                            onClick={addAssessment}
                                            className="p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-[#D67C2C] hover:bg-orange-50 transition-all text-center"
                                        >
                                            <div
                                                className="w-8 h-8 mx-auto mb-2 rounded-lg flex items-center justify-center"
                                                style={{backgroundColor: 'rgba(214, 124, 44, 0.1)'}}>
                                                <FileText className="w-4 h-4 text-[#D67C2C]"/>
                                            </div>
                                            <p className="font-medium text-sm">Add Assessment</p>
                                        </button>
                                        <button
                                            onClick={generateReport}
                                            className="p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-[#D67C2C] hover:bg-orange-50 transition-all text-center"
                                        >
                                            <div
                                                className="w-8 h-8 mx-auto mb-2 rounded-lg flex items-center justify-center"
                                                style={{backgroundColor: 'rgba(214, 124, 44, 0.1)'}}>
                                                <BarChart className="w-4 h-4 text-[#D67C2C]"/>
                                            </div>
                                            <p className="font-medium text-sm">Generate Report</p>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Academic Calendar Tab */}
                        {activeTab === 'calendar' && (
                            <div>
                                <div className="flex justify-between items-center mb-6">
                                    <h2 className="text-2xl font-bold text-[#1D3469]">Academic Calendar 2024-25</h2>
                                    <div className="flex space-x-2">
                                        <button
                                            onClick={addCalendarEvent}
                                            className="bg-[#D67C2C] hover:bg-[#c06a25] text-white px-4 py-2 rounded-lg transition-all"
                                        >
                                            + Add Event
                                        </button>
                                        <select
                                            id="calendarView"
                                            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
                                        >
                                            <option value="year">Year View</option>
                                            <option value="month">Month View</option>
                                            <option value="term">Term View</option>
                                        </select>
                                    </div>
                                </div>

                                {/* Calendar Overview */}
                                <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-8">
                                    <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                                        <h3 className="font-semibold mb-4 text-[#1D3469]">Term 1 (Apr-Aug)</h3>
                                        <div className="space-y-2 text-sm">
                                            <div className="flex justify-between">
                                                <span>Teaching Days:</span>
                                                <span
                                                    className="font-medium cursor-pointer hover:bg-gray-100 px-2 py-1 rounded"
                                                    onClick={(e) => editInline(e.target)}
                                                >75</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span>Holidays:</span>
                                                <span
                                                    className="font-medium cursor-pointer hover:bg-gray-100 px-2 py-1 rounded"
                                                    onClick={(e) => editInline(e.target)}
                                                >15</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span>Assessments:</span>
                                                <span
                                                    className="font-medium cursor-pointer hover:bg-gray-100 px-2 py-1 rounded"
                                                    onClick={(e) => editInline(e.target)}
                                                >8</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span>PTMs:</span>
                                                <span
                                                    className="font-medium cursor-pointer hover:bg-gray-100 px-2 py-1 rounded"
                                                    onClick={(e) => editInline(e.target)}
                                                >2</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                                        <h3 className="font-semibold mb-4 text-[#1D3469]">Term 2 (Sep-Dec)</h3>
                                        <div className="space-y-2 text-sm">
                                            <div className="flex justify-between">
                                                <span>Teaching Days:</span>
                                                <span
                                                    className="font-medium cursor-pointer hover:bg-gray-100 px-2 py-1 rounded"
                                                    onClick={(e) => editInline(e.target)}
                                                >72</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span>Holidays:</span>
                                                <span
                                                    className="font-medium cursor-pointer hover:bg-gray-100 px-2 py-1 rounded"
                                                    onClick={(e) => editInline(e.target)}
                                                >18</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span>Assessments:</span>
                                                <span
                                                    className="font-medium cursor-pointer hover:bg-gray-100 px-2 py-1 rounded"
                                                    onClick={(e) => editInline(e.target)}
                                                >8</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span>PTMs:</span>
                                                <span
                                                    className="font-medium cursor-pointer hover:bg-gray-100 px-2 py-1 rounded"
                                                    onClick={(e) => editInline(e.target)}
                                                >2</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                                        <h3 className="font-semibold mb-4 text-[#1D3469]">Term 3 (Jan-Mar)</h3>
                                        <div className="space-y-2 text-sm">
                                            <div className="flex justify-between">
                                                <span>Teaching Days:</span>
                                                <span
                                                    className="font-medium cursor-pointer hover:bg-gray-100 px-2 py-1 rounded"
                                                    onClick={(e) => editInline(e.target)}
                                                >73</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span>Holidays:</span>
                                                <span
                                                    className="font-medium cursor-pointer hover:bg-gray-100 px-2 py-1 rounded"
                                                    onClick={(e) => editInline(e.target)}
                                                >12</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span>Assessments:</span>
                                                <span
                                                    className="font-medium cursor-pointer hover:bg-gray-100 px-2 py-1 rounded"
                                                    onClick={(e) => editInline(e.target)}
                                                >6</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span>PTMs:</span>
                                                <span
                                                    className="font-medium cursor-pointer hover:bg-gray-100 px-2 py-1 rounded"
                                                    onClick={(e) => editInline(e.target)}
                                                >2</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                                        <h3 className="font-semibold mb-4 text-[#1D3469]">Annual Summary</h3>
                                        <div className="space-y-2 text-sm">
                                            <div className="flex justify-between">
                                                <span>Total Teaching Days:</span>
                                                <span className="font-medium text-[#D67C2C]">220</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span>Total Holidays:</span>
                                                <span className="font-medium text-gray-600">45</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span>Total Assessments:</span>
                                                <span className="font-medium text-orange-600">22</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span>Total PTMs:</span>
                                                <span className="font-medium text-green-600">6</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Key Events Timeline */}
                                <div className="bg-white rounded-xl shadow-sm p-6 mb-8 border border-gray-200">
                                    <h3 className="text-lg font-semibold mb-4 text-[#1D3469]">Key Events &
                                        Milestones</h3>
                                    <div className="space-y-4">
                                        {calendarEvents.map((event, index) => (
                                            <div key={index}
                                                 className="flex items-start space-x-4 p-4 border rounded-lg hover:bg-gray-50 transition-all">
                                                <div
                                                    className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                                                        event.type === 'exam' ? 'bg-red-100' :
                                                            event.type === 'holiday' ? 'bg-gray-100' :
                                                                event.type === 'ptm' ? 'bg-blue-100' : 'bg-green-100'
                                                    }`}>
                                                    <div className={`w-6 h-6 rounded ${
                                                        event.type === 'exam' ? 'bg-red-600' :
                                                            event.type === 'holiday' ? 'bg-gray-600' :
                                                                event.type === 'ptm' ? 'bg-blue-600' : 'bg-green-600'
                                                    }`}></div>
                                                </div>
                                                <div className="flex-1">
                                                    <h4
                                                        className="font-medium cursor-pointer hover:bg-gray-100 px-2 py-1 rounded"
                                                        onClick={(e) => editInline(e.target)}
                                                    >
                                                        {event.title}
                                                    </h4>
                                                    <p
                                                        className="text-sm text-gray-600 cursor-pointer hover:bg-gray-100 px-2 py-1 rounded"
                                                        onClick={(e) => editInline(e.target)}
                                                    >
                                                        {event.description}
                                                    </p>
                                                    <p
                                                        className="text-xs text-gray-500 mt-1 cursor-pointer hover:bg-gray-100 px-1 rounded"
                                                        onClick={(e) => editInline(e.target)}
                                                    >
                                                        {event.date}
                                                    </p>
                                                </div>
                                                <button
                                                    onClick={() => {
                                                        const newEvents = calendarEvents.filter(e => e.date !== event.date || e.title !== event.title);
                                                        setCalendarEvents(newEvents);
                                                        showNotification('Event deleted successfully', 'success');
                                                    }}
                                                    className="text-red-500 hover:text-red-700 text-xs opacity-0 hover:opacity-100 transition-all"
                                                >
                                                    Delete
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Monthly Calendar View */}
                                <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                                    <div className="flex justify-between items-center mb-4">
                                        <h3 className="text-lg font-semibold text-[#1D3469]">Calendar View</h3>
                                        <div className="flex items-center space-x-2">
                                            <button
                                                onClick={previousMonth}
                                                className="p-2 hover:bg-gray-100 rounded"
                                            >
                                                ←
                                            </button>
                                            <span className="font-medium">
                        {['January', 'February', 'March', 'April', 'May', 'June',
                            'July', 'August', 'September', 'October', 'November', 'December'][currentMonthIndex]} {currentYear}
                      </span>
                                            <button
                                                onClick={nextMonth}
                                                className="p-2 hover:bg-gray-100 rounded"
                                            >
                                                →
                                            </button>
                                        </div>
                                    </div>
                                    <div className="grid grid-template-columns: repeat(7, 1fr) gap-1 bg-gray-200">
                                        {renderSampleCalendarMonth()}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Curriculum Planning Tab */}
                        {activeTab === 'curriculum' && (
                            <div>
                                <div className="flex justify-between items-center mb-6">
                                    <h2 className="text-2xl font-bold text-[#1D3469]">Curriculum Planning &
                                        Distribution</h2>
                                    <button
                                        onClick={addCurriculumPlan}
                                        className="bg-[#D67C2C] hover:bg-[#c06a25] text-white px-4 py-2 rounded-lg transition-all"
                                    >
                                        + Add Curriculum Plan
                                    </button>
                                </div>

                                {/* Subject-wise Planning */}
                                <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6 mb-8">
                                    {curriculumPlans.map((plan) => (
                                        <div
                                            key={plan.id}
                                            className={`bg-white rounded-xl shadow-sm p-6 card-hover border border-gray-200 transition-all duration-300 hover:shadow-lg`}
                                        >
                                            <div className="flex justify-between items-start mb-4">
                                                <div>
                                                    <h3 className="font-semibold text-lg text-[#1D3469]">{plan.subject}</h3>
                                                    <p className="text-sm text-gray-600">{plan.grade}</p>
                                                </div>
                                                <span
                                                    className="px-2 py-1 rounded-full text-xs font-medium bg-[#D67C2C] bg-opacity-20 text-[#D67C2C]">
                          {Math.round((plan.completedUnits / plan.totalUnits) * 100)}% Complete
                        </span>
                                            </div>

                                            <div className="space-y-3 mb-4">
                                                <div className="bg-blue-50 p-3 rounded-lg border-l-4 border-blue-500">
                                                    <h4 className="font-medium text-sm mb-1">Term 1</h4>
                                                    <p
                                                        className="text-xs text-gray-600 cursor-pointer hover:bg-blue-100 p-1 rounded"
                                                        onClick={(e) => editInline(e.target)}
                                                    >
                                                        {plan.term1}
                                                    </p>
                                                </div>
                                                <div className="bg-green-50 p-3 rounded-lg border-l-4 border-green-500">
                                                    <h4 className="font-medium text-sm mb-1">Term 2</h4>
                                                    <p
                                                        className="text-xs text-gray-600 cursor-pointer hover:bg-green-100 p-1 rounded"
                                                        onClick={(e) => editInline(e.target)}
                                                    >
                                                        {plan.term2}
                                                    </p>
                                                </div>
                                                <div
                                                    className="bg-yellow-50 p-3 rounded-lg border-l-4 border-yellow-500">
                                                    <h4 className="font-medium text-sm mb-1">Term 3</h4>
                                                    <p
                                                        className="text-xs text-gray-600 cursor-pointer hover:bg-yellow-100 p-1 rounded"
                                                        onClick={(e) => editInline(e.target)}
                                                    >
                                                        {plan.term3}
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                                                <div>
                                                    <span className="text-gray-500">Units:</span>
                                                    <span
                                                        className="font-medium cursor-pointer hover:bg-gray-100 px-2 py-1 rounded"
                                                        onClick={(e) => editInline(e.target)}
                                                    >
                            {plan.completedUnits}/{plan.totalUnits}
                          </span>
                                                </div>
                                                <div>
                                                    <span className="text-gray-500">Hours:</span>
                                                    <span
                                                        className="font-medium cursor-pointer hover:bg-gray-100 px-2 py-1 rounded"
                                                        onClick={(e) => editInline(e.target)}
                                                    >
                            {plan.teachingHours}
                          </span>
                                                </div>
                                            </div>

                                            <div className="flex space-x-2">
                                                <button
                                                    onClick={() => showNotification('Editing curriculum plan...', 'info')}
                                                    className="flex-1 bg-blue-50 text-blue-600 py-2 px-3 rounded-lg text-sm hover:bg-blue-100 transition-all"
                                                >
                                                    Edit Plan
                                                </button>
                                                <button
                                                    onClick={() => showNotification('Viewing progress...', 'info')}
                                                    className="flex-1 bg-green-50 text-green-600 py-2 px-3 rounded-lg text-sm hover:bg-green-100 transition-all"
                                                >
                                                    View Progress
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Learning Outcomes Mapping */}
                                <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                                    <h3 className="text-lg font-semibold mb-4 text-[#1D3469]">Learning Outcomes
                                        Mapping</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                        <select
                                            value={outcomesGradeFilter}
                                            onChange={(e) => setOutcomesGradeFilter(e.target.value)}
                                            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
                                        >
                                            <option value="">All Grades</option>
                                            {outcomesGradeOptions.map(grade => (
                                                <option key={grade} value={grade}>{grade}</option>
                                            ))}
                                        </select>
                                        <select
                                            value={outcomesSubjectFilter}
                                            onChange={(e) => setOutcomesSubjectFilter(e.target.value)}
                                            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
                                        >
                                            <option value="">All Subjects</option>
                                            {outcomesSubjectOptions.map(subject => (
                                                <option key={subject} value={subject}>{subject}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-sm">
                                            <thead>
                                            <tr className="border-b">
                                                <th className="text-left p-3">Grade</th>
                                                <th className="text-left p-3">Subject</th>
                                                <th className="text-left p-3">Unit/Chapter</th>
                                                <th className="text-left p-3">Learning Outcomes</th>
                                                <th className="text-left p-3">Assessment Method</th>
                                                <th className="text-left p-3">Timeline</th>
                                                <th className="text-left p-3">Actions</th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {filteredLearningOutcomes.map((outcome, index) => (
                                                <tr key={index} className="border-b hover:bg-gray-50">
                                                    <td
                                                        className="p-3 cursor-pointer hover:bg-gray-100 rounded"
                                                        onClick={(e) => editInline(e.target)}
                                                    >
                                                        {outcome.grade}
                                                    </td>
                                                    <td
                                                        className="p-3 cursor-pointer hover:bg-gray-100 rounded"
                                                        onClick={(e) => editInline(e.target)}
                                                    >
                                                        {outcome.subject}
                                                    </td>
                                                    <td
                                                        className="p-3 cursor-pointer hover:bg-gray-100 rounded"
                                                        onClick={(e) => editInline(e.target)}
                                                    >
                                                        {outcome.unit}
                                                    </td>
                                                    <td
                                                        className="p-3 cursor-pointer hover:bg-gray-100 rounded"
                                                        onClick={(e) => editInline(e.target)}
                                                    >
                                                        {outcome.outcomes}
                                                    </td>
                                                    <td
                                                        className="p-3 cursor-pointer hover:bg-gray-100 rounded"
                                                        onClick={(e) => editInline(e.target)}
                                                    >
                                                        {outcome.assessment}
                                                    </td>
                                                    <td
                                                        className="p-3 cursor-pointer hover:bg-gray-100 rounded"
                                                        onClick={(e) => editInline(e.target)}
                                                    >
                                                        {outcome.timeline}
                                                    </td>
                                                    <td className="p-3">
                                                        <button
                                                            onClick={() => showNotification('Editing learning outcome...', 'info')}
                                                            className="text-blue-600 hover:text-blue-800 text-sm"
                                                        >
                                                            Edit
                                                        </button>
                                                        <button
                                                            onClick={() => showNotification('Learning outcome deleted', 'success')}
                                                            className="text-red-600 hover:text-red-800 text-sm ml-2"
                                                        >
                                                            Delete
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Pedagogical Strategies Tab */}
                        {activeTab === 'pedagogy' && (
                            <div>
                                <h2 className="text-2xl font-bold mb-6 text-[#1D3469]">Pedagogical Strategies &
                                    Methodologies</h2>

                                {/* Teaching Methodologies */}
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                                    <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                                        <h3 className="text-lg font-semibold mb-4 text-[#1D3469]">Primary Teaching
                                            Methods</h3>
                                        <div className="space-y-4">
                                            {teachingMethods.map((method, index) => (
                                                <div key={index}
                                                     className="border rounded-lg p-4 hover:bg-gray-50 transition-all">
                                                    <h4
                                                        className="font-medium mb-2 cursor-pointer hover:bg-gray-100 px-2 py-1 rounded text-[#D67C2C]"
                                                        onClick={(e) => editInline(e.target)}
                                                    >
                                                        {method.name}
                                                    </h4>
                                                    <p
                                                        className="text-sm text-gray-600 mb-3 cursor-pointer hover:bg-gray-100 p-2 rounded"
                                                        onClick={(e) => editInline(e.target)}
                                                    >
                                                        {method.description}
                                                    </p>
                                                    <div className="grid grid-cols-2 gap-2 text-xs">
                                                        <div>
                                                            <span className="text-gray-500">Grades:</span>
                                                            <span
                                                                className="font-medium cursor-pointer hover:bg-gray-100 px-1 rounded"
                                                                onClick={(e) => editInline(e.target)}
                                                            >
                                {method.grades}
                              </span>
                                                        </div>
                                                        <div>
                                                            <span className="text-gray-500">Frequency:</span>
                                                            <span
                                                                className="font-medium cursor-pointer hover:bg-gray-100 px-1 rounded"
                                                                onClick={(e) => editInline(e.target)}
                                                            >
                                {method.frequency}
                              </span>
                                                        </div>
                                                        <div className="col-span-2">
                                                            <span className="text-gray-500">Subjects:</span>
                                                            <span
                                                                className="font-medium cursor-pointer hover:bg-gray-100 px-1 rounded"
                                                                onClick={(e) => editInline(e.target)}
                                                            >
                                {method.subjects}
                              </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                            <div className="text-center mt-4">
                                                <button
                                                    onClick={() => {
                                                        const name = prompt('Enter teaching method name:');
                                                        if (!name) return;

                                                        const newMethod = {
                                                            name: name,
                                                            description: 'Click to add description',
                                                            grades: 'All Grades',
                                                            frequency: 'Weekly',
                                                            subjects: 'All Subjects'
                                                        };

                                                        setTeachingMethods([...teachingMethods, newMethod]);
                                                        showNotification(`Teaching method "${name}" added successfully!`, 'success');
                                                    }}
                                                    className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded-lg transition-all"
                                                >
                                                    + Add Teaching Method
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                                        <h3 className="text-lg font-semibold mb-4 text-[#1D3469]">Technology
                                            Integration</h3>
                                        <div className="space-y-4">
                                            {[
                                                {
                                                    name: "Digital Learning Resources",
                                                    description: "Curated digital content for interactive learning",
                                                    platforms: "Khan Academy, DIKSHA, PhET",
                                                    grade: "All Grades"
                                                },
                                                {
                                                    name: "Learning Management System",
                                                    description: "Online platform for assignments and assessments",
                                                    platforms: "Google Classroom",
                                                    grade: "Grades 3-12"
                                                },
                                                {
                                                    name: "Interactive Smart Boards",
                                                    description: "Technology-enhanced classroom teaching",
                                                    platforms: "SmartClass+, Promethean",
                                                    grade: "All Grades"
                                                },
                                                {
                                                    name: "Student Response Systems",
                                                    description: "Real-time assessment and feedback",
                                                    platforms: "Kahoot, Quizizz",
                                                    grade: "Grades 4-12"
                                                }
                                            ].map((tech, index) => (
                                                <div key={index}
                                                     className="border rounded-lg p-4 hover:bg-gray-50 transition-all">
                                                    <h4
                                                        className="font-medium mb-2 cursor-pointer hover:bg-gray-100 px-2 py-1 rounded text-[#D67C2C]"
                                                        onClick={(e) => editInline(e.target)}
                                                    >
                                                        {tech.name}
                                                    </h4>
                                                    <p
                                                        className="text-sm text-gray-600 mb-3 cursor-pointer hover:bg-gray-100 p-2 rounded"
                                                        onClick={(e) => editInline(e.target)}
                                                    >
                                                        {tech.description}
                                                    </p>
                                                    <div className="grid grid-cols-2 gap-2 text-xs">
                                                        <div>
                                                            <span className="text-gray-500">Platforms:</span>
                                                            <span
                                                                className="font-medium cursor-pointer hover:bg-gray-100 px-1 rounded"
                                                                onClick={(e) => editInline(e.target)}
                                                            >
                                {tech.platforms}
                              </span>
                                                        </div>
                                                        <div>
                                                            <span className="text-gray-500">Grades:</span>
                                                            <span
                                                                className="font-medium cursor-pointer hover:bg-gray-100 px-1 rounded"
                                                                onClick={(e) => editInline(e.target)}
                                                            >
                                {tech.grade}
                              </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                            <div className="text-center mt-4">
                                                <button
                                                    onClick={() => showNotification('Adding new technology integration...', 'info')}
                                                    className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded-lg transition-all"
                                                >
                                                    + Add Technology Tool
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Grade-wise Pedagogical Framework */}
                                <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                                    <h3 className="text-lg font-semibold mb-4 text-[#1D3469]">Grade-wise Pedagogical
                                        Framework</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                                        {pedagogicalFrameworks.map((framework, index) => (
                                            <div key={index} className="bg-gray-50 p-4 rounded-lg">
                                                <h4
                                                    className="font-medium text-[#D67C2C] mb-2 cursor-pointer hover:bg-blue-100 px-2 py-1 rounded"
                                                    onClick={(e) => editInline(e.target)}
                                                >
                                                    {framework.grade}
                                                </h4>
                                                <div className="space-y-2 text-sm">
                                                    <div>
                                                        <span className="text-gray-500">Focus:</span>
                                                        <span
                                                            className="font-medium cursor-pointer hover:bg-gray-100 px-1 rounded"
                                                            onClick={(e) => editInline(e.target)}
                                                        >
                              {framework.focus}
                            </span>
                                                    </div>
                                                    <div>
                                                        <span className="text-gray-500">Methods:</span>
                                                        <span
                                                            className="cursor-pointer hover:bg-gray-100 px-1 rounded"
                                                            onClick={(e) => editInline(e.target)}
                                                        >
                              {framework.methods}
                            </span>
                                                    </div>
                                                    <div>
                                                        <span className="text-gray-500">Assessment:</span>
                                                        <span
                                                            className="cursor-pointer hover:bg-gray-100 px-1 rounded"
                                                            onClick={(e) => editInline(e.target)}
                                                        >
                              {framework.assessment}
                            </span>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Assessment Framework Tab */}
                        {activeTab === 'assessment' && (
                            <div>
                                <div className="flex justify-between items-center mb-6">
                                    <h2 className="text-2xl font-bold text-[#1D3469]">Assessment Framework &
                                        Schedule</h2>
                                    <button
                                        onClick={addAssessment}
                                        className="bg-[#D67C2C] hover:bg-[#c06a25] text-white px-4 py-2 rounded-lg transition-all"
                                    >
                                        + Add Assessment
                                    </button>
                                </div>

                                {/* Assessment Overview */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                                    <div className="bg-blue-50 p-6 rounded-xl">
                                        <h3 className="font-semibold text-blue-700 mb-3">Formative Assessment (FA)</h3>
                                        <div className="space-y-2 text-sm">
                                            <div className="flex justify-between">
                                                <span>Frequency:</span>
                                                <span
                                                    className="font-medium cursor-pointer hover:bg-blue-100 px-2 py-1 rounded"
                                                    onClick={(e) => editInline(e.target)}
                                                >
                          Weekly
                        </span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span>Weightage:</span>
                                                <span
                                                    className="font-medium cursor-pointer hover:bg-blue-100 px-2 py-1 rounded"
                                                    onClick={(e) => editInline(e.target)}
                                                >
                          40%
                        </span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span>Methods:</span>
                                                <span
                                                    className="font-medium cursor-pointer hover:bg-blue-100 px-2 py-1 rounded"
                                                    onClick={(e) => editInline(e.target)}
                                                >
                          Observation, Quiz, Project
                        </span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-green-50 p-6 rounded-xl">
                                        <h3 className="font-semibold text-green-700 mb-3">Summative Assessment (SA)</h3>
                                        <div className="space-y-2 text-sm">
                                            <div className="flex justify-between">
                                                <span>Frequency:</span>
                                                <span
                                                    className="font-medium cursor-pointer hover:bg-green-100 px-2 py-1 rounded"
                                                    onClick={(e) => editInline(e.target)}
                                                >
                          Term-end
                        </span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span>Weightage:</span>
                                                <span
                                                    className="font-medium cursor-pointer hover:bg-green-100 px-2 py-1 rounded"
                                                    onClick={(e) => editInline(e.target)}
                                                >
                          60%
                        </span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span>Methods:</span>
                                                <span
                                                    className="font-medium cursor-pointer hover:bg-green-100 px-2 py-1 rounded"
                                                    onClick={(e) => editInline(e.target)}
                                                >
                          Written, Practical, Oral
                        </span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-orange-50 p-6 rounded-xl">
                                        <h3 className="font-semibold text-orange-700 mb-3">Competency Assessment</h3>
                                        <div className="space-y-2 text-sm">
                                            <div className="flex justify-between">
                                                <span>Frequency:</span>
                                                <span
                                                    className="font-medium cursor-pointer hover:bg-orange-100 px-2 py-1 rounded"
                                                    onClick={(e) => editInline(e.target)}
                                                >
                          Monthly
                        </span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span>Focus:</span>
                                                <span
                                                    className="font-medium cursor-pointer hover:bg-orange-100 px-2 py-1 rounded"
                                                    onClick={(e) => editInline(e.target)}
                                                >
                          Skills & Application
                        </span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span>Methods:</span>
                                                <span
                                                    className="font-medium cursor-pointer hover:bg-orange-100 px-2 py-1 rounded"
                                                    onClick={(e) => editInline(e.target)}
                                                >
                          Portfolio, Performance
                        </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Assessment Schedule */}
                                <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                                    <h3 className="text-lg font-semibold mb-4 text-[#1D3469]">Assessment Schedule</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                                        <select
                                            value={assessmentTypeFilter}
                                            onChange={(e) => setAssessmentTypeFilter(e.target.value)}
                                            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
                                        >
                                            <option value="">All Types</option>
                                            {assessmentTypeOptions.map(type => (
                                                <option key={type} value={type}>{type}</option>
                                            ))}
                                        </select>
                                        <select
                                            value={assessmentGradeFilter}
                                            onChange={(e) => setAssessmentGradeFilter(e.target.value)}
                                            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
                                        >
                                            <option value="">All Grades</option>
                                            {assessmentGradeOptions.map(grade => (
                                                <option key={grade} value={grade}>{grade}</option>
                                            ))}
                                        </select>
                                        <select
                                            value={assessmentStatusFilter}
                                            onChange={(e) => setAssessmentStatusFilter(e.target.value)}
                                            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
                                        >
                                            <option value="">All Status</option>
                                            {assessmentStatusOptions.map(status => (
                                                <option key={status} value={status}>{status}</option>
                                            ))}
                                        </select>
                                        <div>
                                            <label className="block text-xs text-gray-500 mb-1">Date From</label>
                                            <input
                                                type="date"
                                                value={assessmentDateStartFilter}
                                                onChange={(e) => setAssessmentDateStartFilter(e.target.value)}
                                                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-gray-500 mb-1">Date To</label>
                                            <input
                                                type="date"
                                                value={assessmentDateEndFilter}
                                                onChange={(e) => setAssessmentDateEndFilter(e.target.value)}
                                                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
                                            />
                                        </div>
                                    </div>
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-sm">
                                            <thead>
                                            <tr className="border-b">
                                                <th className="text-left p-3">Date</th>
                                                <th className="text-left p-3">Type</th>
                                                <th className="text-left p-3">Grade</th>
                                                <th className="text-left p-3">Subject</th>
                                                <th className="text-left p-3">Duration</th>
                                                <th className="text-left p-3">Weightage</th>
                                                <th className="text-left p-3">Status</th>
                                                <th className="text-left p-3">Actions</th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {filteredAssessments.map((assessment, index) => (
                                                <tr key={index} className="border-b hover:bg-gray-50">
                                                    <td
                                                        className="p-3 cursor-pointer hover:bg-gray-100 rounded"
                                                        onClick={(e) => editInline(e.target)}
                                                    >
                                                        {assessment.date}
                                                    </td>
                                                    <td
                                                        className="p-3 cursor-pointer hover:bg-gray-100 rounded"
                                                        onClick={(e) => editInline(e.target)}
                                                    >
                                                        {assessment.type}
                                                    </td>
                                                    <td
                                                        className="p-3 cursor-pointer hover:bg-gray-100 rounded"
                                                        onClick={(e) => editInline(e.target)}
                                                    >
                                                        {assessment.grade}
                                                    </td>
                                                    <td
                                                        className="p-3 cursor-pointer hover:bg-gray-100 rounded"
                                                        onClick={(e) => editInline(e.target)}
                                                    >
                                                        {assessment.subject}
                                                    </td>
                                                    <td
                                                        className="p-3 cursor-pointer hover:bg-gray-100 rounded"
                                                        onClick={(e) => editInline(e.target)}
                                                    >
                                                        {assessment.duration}
                                                    </td>
                                                    <td
                                                        className="p-3 cursor-pointer hover:bg-gray-100 rounded"
                                                        onClick={(e) => editInline(e.target)}
                                                    >
                                                        {assessment.weightage}
                                                    </td>
                                                    <td className="p-3">
                              <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                {assessment.status}
                              </span>
                                                    </td>
                                                    <td className="p-3">
                                                        <button
                                                            onClick={() => showNotification('Editing assessment...', 'info')}
                                                            className="text-blue-600 hover:text-blue-800 text-sm"
                                                        >
                                                            Edit
                                                        </button>
                                                        <button
                                                            onClick={() => {
                                                                setAssessments(assessments.filter(a => a !== assessment));
                                                                showNotification('Assessment deleted successfully', 'success');
                                                            }}
                                                            className="text-red-600 hover:text-red-800 text-sm ml-2"
                                                        >
                                                            Delete
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Co-Curricular Tab */}
                        {activeTab === 'cocurricular' && (
                            <div>
                                <div className="flex justify-between items-center mb-6">
                                    <h2 className="text-2xl font-bold text-[#1D3469]">Co-Curricular & Holistic
                                        Development</h2>
                                    <button
                                        onClick={addCoCurricularActivity}
                                        className="bg-[#D67C2C] hover:bg-[#c06a25] text-white px-4 py-2 rounded-lg transition-all"
                                    >
                                        + Add Activity
                                    </button>
                                </div>

                                {/* Activity Categories */}
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                                    <div className="bg-blue-50 p-4 rounded-lg text-center">
                                        <h3 className="font-semibold text-blue-700">Arts & Culture</h3>
                                        <p className="text-2xl font-bold text-blue-600">{activityCounts.artsActivities}</p>
                                        <p className="text-sm text-gray-600">Activities Planned</p>
                                    </div>
                                    <div className="bg-green-50 p-4 rounded-lg text-center">
                                        <h3 className="font-semibold text-green-700">Sports & Fitness</h3>
                                        <p className="text-2xl font-bold text-green-600">{activityCounts.sportsActivities}</p>
                                        <p className="text-sm text-gray-600">Activities Planned</p>
                                    </div>
                                    <div className="bg-orange-50 p-4 rounded-lg text-center">
                                        <h3 className="font-semibold text-orange-700">Community Service</h3>
                                        <p className="text-2xl font-bold text-orange-600">{activityCounts.communityActivities}</p>
                                        <p className="text-sm text-gray-600">Projects Planned</p>
                                    </div>
                                    <div className="bg-purple-50 p-4 rounded-lg text-center">
                                        <h3 className="font-semibold text-purple-700">Clubs & Societies</h3>
                                        <p className="text-2xl font-bold text-purple-600">{activityCounts.clubActivities}</p>
                                        <p className="text-sm text-gray-600">Regular Sessions</p>
                                    </div>
                                </div>

                                {/* Activities List */}
                                <div className="space-y-6">
                                    {coCurricularActivities.map(activity => (
                                        <div key={activity.id}
                                             className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                                            <div className="flex justify-between items-start mb-4">
                                                <div>
                                                    <h3
                                                        className="font-semibold text-lg cursor-pointer hover:bg-gray-100 px-2 py-1 rounded text-[#1D3469]"
                                                        onClick={(e) => editInline(e.target)}
                                                    >
                                                        {activity.name}
                                                    </h3>
                                                    <p
                                                        className="text-sm text-gray-600 cursor-pointer hover:bg-gray-100 px-2 py-1 rounded"
                                                        onClick={(e) => editInline(e.target)}
                                                    >
                                                        {activity.category}
                                                    </p>
                                                </div>
                                                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                                    activity.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                                                }`}>
                          {activity.status}
                        </span>
                                            </div>
                                            <p
                                                className="text-gray-600 text-sm mb-4 cursor-pointer hover:bg-gray-100 p-2 rounded"
                                                onClick={(e) => editInline(e.target)}
                                            >
                                                {activity.description}
                                            </p>

                                            <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                                                <div>
                                                    <span className="text-gray-500">Date:</span>
                                                    <span
                                                        className="font-medium cursor-pointer hover:bg-gray-100 px-2 py-1 rounded"
                                                        onClick={(e) => editInline(e.target)}
                                                    >
                            {activity.date}
                          </span>
                                                </div>
                                                <div>
                                                    <span className="text-gray-500">Participants:</span>
                                                    <span
                                                        className="font-medium cursor-pointer hover:bg-gray-100 px-2 py-1 rounded"
                                                        onClick={(e) => editInline(e.target)}
                                                    >
                            {activity.participants}
                          </span>
                                                </div>
                                            </div>

                                            <div className="flex space-x-2">
                                                <button
                                                    onClick={() => showNotification('Editing activity...', 'info')}
                                                    className="bg-blue-50 text-blue-600 py-2 px-3 rounded-lg text-sm hover:bg-blue-100 transition-all"
                                                >
                                                    Edit Activity
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        setCoCurricularActivities(coCurricularActivities.filter(a => a.id !== activity.id));
                                                        showNotification('Activity deleted successfully', 'success');
                                                    }}
                                                    className="bg-red-50 text-red-600 py-2 px-3 rounded-lg text-sm hover:bg-red-100 transition-all"
                                                >
                                                    Delete
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Inclusion & Support Tab */}
                        {activeTab === 'inclusion' && (
                            <div>
                                <h2 className="text-2xl font-bold mb-6 text-[#1D3469]">Inclusion & Remediation
                                    Support</h2>

                                {/* Support Categories */}
                                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
                                    <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                                        <h3 className="text-lg font-semibold mb-4 text-blue-600">Special Educational
                                            Needs (SEN)</h3>
                                        <div className="space-y-3">
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">CWSN Students:</span>
                                                <span
                                                    className="font-medium cursor-pointer hover:bg-gray-100 px-2 py-1 rounded"
                                                    onClick={(e) => editInline(e.target)}
                                                >
                          37
                        </span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Support Staff:</span>
                                                <span
                                                    className="font-medium cursor-pointer hover:bg-gray-100 px-2 py-1 rounded"
                                                    onClick={(e) => editInline(e.target)}
                                                >
                          3
                        </span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">IEP Plans:</span>
                                                <span
                                                    className="font-medium cursor-pointer hover:bg-gray-100 px-2 py-1 rounded"
                                                    onClick={(e) => editInline(e.target)}
                                                >
                          37
                        </span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Assistive Tools:</span>
                                                <span
                                                    className="font-medium cursor-pointer hover:bg-gray-100 px-2 py-1 rounded"
                                                    onClick={(e) => editInline(e.target)}
                                                >
                          Available
                        </span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                                        <h3 className="text-lg font-semibold mb-4 text-green-600">Remedial Support</h3>
                                        <div className="space-y-3">
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">At-Risk Students:</span>
                                                <span
                                                    className="font-medium cursor-pointer hover:bg-gray-100 px-2 py-1 rounded"
                                                    onClick={(e) => editInline(e.target)}
                                                >
                          89
                        </span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Remedial Classes:</span>
                                                <span
                                                    className="font-medium cursor-pointer hover:bg-gray-100 px-2 py-1 rounded"
                                                    onClick={(e) => editInline(e.target)}
                                                >
                          Daily
                        </span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Peer Tutoring:</span>
                                                <span
                                                    className="font-medium cursor-pointer hover:bg-gray-100 px-2 py-1 rounded"
                                                    onClick={(e) => editInline(e.target)}
                                                >
                          Active
                        </span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Parent Involvement:</span>
                                                <span
                                                    className="font-medium cursor-pointer hover:bg-gray-100 px-2 py-1 rounded"
                                                    onClick={(e) => editInline(e.target)}
                                                >
                          Weekly
                        </span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                                        <h3 className="text-lg font-semibold mb-4 text-orange-600">Multilingual
                                            Support</h3>
                                        <div className="space-y-3">
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Languages Supported:</span>
                                                <span
                                                    className="font-medium cursor-pointer hover:bg-gray-100 px-2 py-1 rounded"
                                                    onClick={(e) => editInline(e.target)}
                                                >
                          5
                        </span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Mother Tongue Classes:</span>
                                                <span
                                                    className="font-medium cursor-pointer hover:bg-gray-100 px-2 py-1 rounded"
                                                    onClick={(e) => editInline(e.target)}
                                                >
                          3/week
                        </span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Translation Support:</span>
                                                <span
                                                    className="font-medium cursor-pointer hover:bg-gray-100 px-2 py-1 rounded"
                                                    onClick={(e) => editInline(e.target)}
                                                >
                          Available
                        </span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Cultural Programs:</span>
                                                <span
                                                    className="font-medium cursor-pointer hover:bg-gray-100 px-2 py-1 rounded"
                                                    className="font-medium cursor-pointer hover:bg-gray-100 px-2 py-1 rounded"
                                                    onClick={(e) => editInline(e.target)}
                                                >
                          Monthly
                        </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Support Programs */}
                                <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                                    <h3 className="text-lg font-semibold mb-4 text-[#1D3469]">Support Programs &
                                        Interventions</h3>
                                    <div className="space-y-4">
                                        {supportPrograms.map((program, index) => (
                                            <div key={index}
                                                 className="border rounded-lg p-4 hover:bg-gray-50 transition-all">
                                                <h4
                                                    className="font-medium mb-2 cursor-pointer hover:bg-gray-100 px-2 py-1 rounded text-[#D67C2C]"
                                                    onClick={(e) => editInline(e.target)}
                                                >
                                                    {program.name}
                                                </h4>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                                    <div>
                                                        <span className="text-gray-500">Target:</span>
                                                        <span
                                                            className="cursor-pointer hover:bg-gray-100 px-1 rounded"
                                                            onClick={(e) => editInline(e.target)}
                                                        >
                              {program.target}
                            </span>
                                                    </div>
                                                    <div>
                                                        <span className="text-gray-500">Frequency:</span>
                                                        <span
                                                            className="cursor-pointer hover:bg-gray-100 px-1 rounded"
                                                            onClick={(e) => editInline(e.target)}
                                                        >
                              {program.frequency}
                            </span>
                                                    </div>
                                                    <div>
                                                        <span className="text-gray-500">Staff:</span>
                                                        <span
                                                            className="cursor-pointer hover:bg-gray-100 px-1 rounded"
                                                            onClick={(e) => editInline(e.target)}
                                                        >
                              {program.staff}
                            </span>
                                                    </div>
                                                    <div>
                                                        <span className="text-gray-500">Resources:</span>
                                                        <span
                                                            className="cursor-pointer hover:bg-gray-100 px-1 rounded"
                                                            onClick={(e) => editInline(e.target)}
                                                        >
                              {program.resources}
                            </span>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                        <div className="text-center mt-4">
                                            <button
                                                onClick={() => {
                                                    const name = prompt('Enter support program name:');
                                                    if (!name) return;

                                                    const newProgram = {
                                                        name: name,
                                                        target: "Click to add target group",
                                                        frequency: "Weekly",
                                                        staff: "Assigned staff",
                                                        resources: "Required resources"
                                                    };

                                                    setSupportPrograms([...supportPrograms, newProgram]);
                                                    showNotification(`Support program "${name}" added successfully!`, 'success');
                                                }}
                                                className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded-lg transition-all"
                                            >
                                                + Add Support Program
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Teacher Allocation Tab */}
                        {activeTab === 'teachers' && (
                            <div>
                                <div className="flex justify-between items-center mb-6">
                                    <h2 className="text-2xl font-bold text-[#1D3469]">Teacher Role Allocation &
                                        Responsibilities</h2>
                                    <button
                                        onClick={assignTeacherRole}
                                        className="bg-[#D67C2C] hover:bg-[#c06a25] text-white px-4 py-2 rounded-lg transition-all"
                                    >
                                        + Assign Role
                                    </button>
                                </div>

                                {/* Teacher Overview */}
                                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                                    <div className="bg-blue-50 p-4 rounded-lg text-center">
                                        <h3 className="font-semibold text-blue-700">Total Teachers</h3>
                                        <p className="text-2xl font-bold text-blue-600">42</p>
                                        <p className="text-sm text-gray-600">Teaching Staff</p>
                                    </div>
                                    <div className="bg-green-50 p-4 rounded-lg text-center">
                                        <h3 className="font-semibold text-green-700">Subject Allocation</h3>
                                        <p className="text-2xl font-bold text-green-600">100%</p>
                                        <p className="text-sm text-gray-600">Complete</p>
                                    </div>
                                    <div className="bg-orange-50 p-4 rounded-lg text-center">
                                        <h3 className="font-semibold text-orange-700">Additional Duties</h3>
                                        <p className="text-2xl font-bold text-orange-600">38</p>
                                        <p className="text-sm text-gray-600">Assigned</p>
                                    </div>
                                    <div className="bg-purple-50 p-4 rounded-lg text-center">
                                        <h3 className="font-semibold text-purple-700">Training Hours</h3>
                                        <p className="text-2xl font-bold text-purple-600">40</p>
                                        <p className="text-sm text-gray-600">Per Teacher/Year</p>
                                    </div>
                                </div>

                                {/* Teacher Allocation Table */}
                                <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                                    <h3 className="text-lg font-semibold mb-4 text-[#1D3469]">Teacher Allocation
                                        Matrix</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                        <select
                                            value={teacherSubjectFilter}
                                            onChange={(e) => setTeacherSubjectFilter(e.target.value)}
                                            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
                                        >
                                            <option value="">All Primary Subjects</option>
                                            {teacherSubjectOptions.map(subject => (
                                                <option key={subject} value={subject}>{subject}</option>
                                            ))}
                                        </select>
                                        <select
                                            value={teacherTrainingFilter}
                                            onChange={(e) => setTeacherTrainingFilter(e.target.value)}
                                            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
                                        >
                                            <option value="">All Training Status</option>
                                            {teacherTrainingOptions.map(training => (
                                                <option key={training} value={training}>{training}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-sm">
                                            <thead>
                                            <tr className="border-b">
                                                <th className="text-left p-3">Teacher Name</th>
                                                <th className="text-left p-3">Primary Subject</th>
                                                <th className="text-left p-3">Classes Assigned</th>
                                                <th className="text-left p-3">Additional Duties</th>
                                                <th className="text-left p-3">Training Status</th>
                                                <th className="text-left p-3">Workload</th>
                                                <th className="text-left p-3">Actions</th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {filteredTeacherAllocation.map((teacher, index) => (
                                                <tr key={index} className="border-b hover:bg-gray-50">
                                                    <td
                                                        className="p-3 cursor-pointer hover:bg-gray-100 rounded"
                                                        onClick={(e) => editInline(e.target)}
                                                    >
                                                        {teacher.name}
                                                    </td>
                                                    <td
                                                        className="p-3 cursor-pointer hover:bg-gray-100 rounded"
                                                        onClick={(e) => editInline(e.target)}
                                                    >
                                                        {teacher.subject}
                                                    </td>
                                                    <td
                                                        className="p-3 cursor-pointer hover:bg-gray-100 rounded"
                                                        onClick={(e) => editInline(e.target)}
                                                    >
                                                        {teacher.classes}
                                                    </td>
                                                    <td
                                                        className="p-3 cursor-pointer hover:bg-gray-100 rounded"
                                                        onClick={(e) => editInline(e.target)}
                                                    >
                                                        {teacher.duties}
                                                    </td>
                                                    <td className="p-3">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  teacher.training === 'Completed' ? 'bg-green-100 text-green-800' :
                                      'bg-yellow-100 text-yellow-800'
                              }`}>
                                {teacher.training}
                              </span>
                                                    </td>
                                                    <td
                                                        className="p-3 cursor-pointer hover:bg-gray-100 rounded"
                                                        onClick={(e) => editInline(e.target)}
                                                    >
                                                        {teacher.workload}
                                                    </td>
                                                    <td className="p-3">
                                                        <button
                                                            onClick={() => showNotification('Editing teacher allocation...', 'info')}
                                                            className="text-blue-600 hover:text-blue-800 text-sm"
                                                        >
                                                            Edit
                                                        </button>
                                                        <button
                                                            onClick={() => showNotification('Reassigning teacher...', 'info')}
                                                            className="text-green-600 hover:text-green-800 text-sm ml-2"
                                                        >
                                                            Reassign
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="text-center mt-6">
                                        <button
                                            onClick={() => {
                                                const name = prompt('Enter teacher name:');
                                                if (!name) return;

                                                const subject = prompt('Enter primary subject:');
                                                if (!subject) return;

                                                const newTeacher = {
                                                    name: name,
                                                    subject: subject,
                                                    classes: "Not assigned",
                                                    duties: "Not assigned",
                                                    training: "Not Started",
                                                    workload: "0%"
                                                };

                                                setTeacherAllocation([...teacherAllocation, newTeacher]);
                                                showNotification(`Teacher "${name}" added successfully!`, 'success');
                                            }}
                                            className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded-lg transition-all"
                                        >
                                            + Add New Teacher
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Monitoring & Review Tab */}
                        {activeTab === 'monitoring' && (
                            <div>
                                <h2 className="text-2xl font-bold mb-6 text-[#1D3469]">Monitoring & Review
                                    Framework</h2>

                                {/* Review Schedule */}
                                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
                                    <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                                        <h3 className="text-lg font-semibold mb-4 text-blue-600">Monthly Reviews</h3>
                                        <div className="space-y-3">
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Frequency:</span>
                                                <span
                                                    className="font-medium cursor-pointer hover:bg-gray-100 px-2 py-1 rounded"
                                                    onClick={(e) => editInline(e.target)}
                                                >
                          1st Monday
                        </span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Focus:</span>
                                                <span
                                                    className="font-medium cursor-pointer hover:bg-gray-100 px-2 py-1 rounded"
                                                    onClick={(e) => editInline(e.target)}
                                                >
                          Curriculum Progress
                        </span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Participants:</span>
                                                <span
                                                    className="font-medium cursor-pointer hover:bg-gray-100 px-2 py-1 rounded"
                                                    onClick={(e) => editInline(e.target)}
                                                >
                          Subject Heads
                        </span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Duration:</span>
                                                <span
                                                    className="font-medium cursor-pointer hover:bg-gray-100 px-2 py-1 rounded"
                                                    onClick={(e) => editInline(e.target)}
                                                >
                          2 hours
                        </span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                                        <h3 className="text-lg font-semibold mb-4 text-green-600">Quarterly
                                            Assessments</h3>
                                        <div className="space-y-3">
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Frequency:</span>
                                                <span
                                                    className="font-medium cursor-pointer hover:bg-gray-100 px-2 py-1 rounded"
                                                    onClick={(e) => editInline(e.target)}
                                                >
                          End of Term
                        </span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Focus:</span>
                                                <span
                                                    className="font-medium cursor-pointer hover:bg-gray-100 px-2 py-1 rounded"
                                                    onClick={(e) => editInline(e.target)}
                                                >
                          Learning Outcomes
                        </span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Participants:</span>
                                                <span
                                                    className="font-medium cursor-pointer hover:bg-gray-100 px-2 py-1 rounded"
                                                    onClick={(e) => editInline(e.target)}
                                                >
                          All Teachers
                        </span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Duration:</span>
                                                <span
                                                    className="font-medium cursor-pointer hover:bg-gray-100 px-2 py-1 rounded"
                                                    onClick={(e) => editInline(e.target)}
                                                >
                          Full Day
                        </span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                                        <h3 className="text-lg font-semibold mb-4 text-orange-600">Annual
                                            Evaluation</h3>
                                        <div className="space-y-3">
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Frequency:</span>
                                                <span
                                                    className="font-medium cursor-pointer hover:bg-gray-100 px-2 py-1 rounded"
                                                    onClick={(e) => editInline(e.target)}
                                                >
                          March 2025
                        </span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Focus:</span>
                                                <span
                                                    className="font-medium cursor-pointer hover:bg-gray-100 px-2 py-1 rounded"
                                                    onClick={(e) => editInline(e.target)}
                                                >
                          APP Effectiveness
                        </span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Participants:</span>
                                                <span
                                                    className="font-medium cursor-pointer hover:bg-gray-100 px-2 py-1 rounded"
                                                    onClick={(e) => editInline(e.target)}
                                                >
                          All Stakeholders
                        </span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Duration:</span>
                                                <span
                                                    className="font-medium cursor-pointer hover:bg-gray-100 px-2 py-1 rounded"
                                                    onClick={(e) => editInline(e.target)}
                                                >
                          2 Days
                        </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Progress Indicators */}
                                <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                                    <h3 className="text-lg font-semibold mb-4 text-[#1D3469]">Progress Indicators &
                                        Metrics</h3>
                                    <div className="space-y-4">
                                        {progressIndicators.map((indicator, index) => (
                                            <div key={index}
                                                 className="flex justify-between items-center p-4 border rounded-lg hover:bg-gray-50 transition-all">
                                                <div className="flex-1">
                                                    <h4
                                                        className="font-medium cursor-pointer hover:bg-gray-100 px-2 py-1 rounded text-[#D67C2C]"
                                                        onClick={(e) => editInline(e.target)}
                                                    >
                                                        {indicator.name}
                                                    </h4>
                                                    <p
                                                        className="text-sm text-gray-600 cursor-pointer hover:bg-gray-100 px-2 py-1 rounded"
                                                        onClick={(e) => editInline(e.target)}
                                                    >
                                                        {indicator.description}
                                                    </p>
                                                    <div className="text-sm text-gray-500 mt-1">
                                                        <span>Current: </span>
                                                        <span
                                                            className="cursor-pointer hover:bg-gray-100 px-1 rounded"
                                                            onClick={(e) => editInline(e.target)}
                                                        >
                              {indicator.current}
                            </span>
                                                        <span> | Target: </span>
                                                        <span
                                                            className="cursor-pointer hover:bg-gray-100 px-1 rounded"
                                                            onClick={(e) => editInline(e.target)}
                                                        >
                              {indicator.target}
                            </span>
                                                    </div>
                                                </div>
                                                <div className="flex items-center space-x-2">
                                                    <button
                                                        onClick={() => toggleIndicatorTrend(index)}
                                                        className={`text-sm ${
                                                            indicator.trend === 'up' ? 'text-green-600' :
                                                                indicator.trend === 'down' ? 'text-red-600' : 'text-gray-600'
                                                        } hover:bg-gray-100 px-2 py-1 rounded`}
                                                    >
                                                        {indicator.trend === 'up' ? '↗' :
                                                            indicator.trend === 'down' ? '↘' : '→'}
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                        <div className="text-center mt-4">
                                            <button
                                                onClick={() => {
                                                    const name = prompt('Enter indicator name:');
                                                    if (!name) return;

                                                    const description = prompt('Enter description:');
                                                    if (!description) return;

                                                    const newIndicator = {
                                                        name: name,
                                                        description: description,
                                                        current: "0%",
                                                        target: "100%",
                                                        trend: "stable"
                                                    };

                                                    setProgressIndicators([...progressIndicators, newIndicator]);
                                                    showNotification(`Indicator "${name}" added successfully!`, 'success');
                                                }}
                                                className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded-lg transition-all"
                                            >
                                                + Add Progress Indicator
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </main>
                </div>
            </div>
        </>
    );
};

export default AnnualPedagogicalPlan;